import { createLogger } from '../utils/debugLogger';
const l = createLogger('baseModal.js');
import { createSelectors } from '../utils/selectorUtils';
import { ajax } from '../utils/ajaxUtils';

export class BaseModal {
    #isLoaded = false;
    #isLoading = false;
    #modalName;
    #queryStringManager;

    static ModalSelectors = createSelectors({
        TRIGGER: (modalName) => `data-ts-trigger="${modalName}"`,
        MODAL_CONTAINER: (modalName) => `data-ts-modal="${modalName}"`,
        CONTENT_CONTAINER: (modalName) => `data-ts-modal-content="${modalName}"`,
        CLEAR_BUTTON: (modalName) => `data-ts-clear="${modalName}"`,
    });

    constructor(modalName, queryStringManager = null) {
        this.#modalName = modalName;
        this.#isLoaded = false;
        this.#isLoading = false;
        this._eventHandlers = new Map();
        this.#queryStringManager = queryStringManager;
    }

    // Static factory method that enforces requirements
    static create(modalName, queryStringManager = null) {
        if (!this.Selectors) {
            throw new Error('Modal class must define static Selectors');
        }
        if (!this.Config) {
            throw new Error('Modal class must define static Config');
        }
        return new this(modalName, queryStringManager);
    }

    get isLoaded() {
        return this.#isLoaded;
    }

    get isLoading() {
        return this.#isLoading;
    }

    get modalName() {
        return this.#modalName;
    }

    attachHandler(name, handler, selector, eventTypes = ['click']) {
        name = `${this.#modalName}:${name}`;
        if (this._eventHandlers.has(name)) {
            l.log(`${this.#modalName}: Handler '${name}' already attached`);
            return;
        }

        const elements = document.querySelectorAll(selector);
        if (elements.length === 0) {
            l.log(`${this.#modalName}: No elements found for selector '${selector}'. Handler '${name}' not attached.`);
            return;
        }

        this._eventHandlers.set(name, {
            handler,
            selector,
            eventTypes,
            elements
        });

        l.log(`${this.#modalName}: Attaching ${name} handlers to ${elements.length} elements on ${selector} for events: ${eventTypes.join(', ')}`);

        elements.forEach(element => {
            eventTypes.forEach(eventType => {
                element.addEventListener(eventType, handler);
            });
        });
    }

    attachModalTriggerEvent() {
        const triggerContainerQuery = this.constructor.ModalSelectors.TRIGGER(this.#modalName);

        // Using attachHandler for trigger events
        this.attachHandler(
            'modalTrigger',
            this.loadModalContent,
            triggerContainerQuery,
            ['mouseenter', 'click']
        );
    }

    clearSelected = () => {
        l.log(`${this.#modalName}: clearSelected`);

        document.querySelectorAll(this.constructor.Selectors.INPUT.SELECTED)
            .forEach(cb => {
                cb.checked = false;
            });
    }

    loadModalContent = async (evt) => {
        l.log(`${this.#modalName}: loadModalContent`, evt.type);

        // Prevent default for touch/click but not mouseenter
        if (evt.type !== 'mouseenter') {
            evt.preventDefault();
        }

        if (this.#isLoaded || this.#isLoading) {
            l.log(`${this.#modalName}: Already loaded ${this.#isLoaded} or loading ${this.isLoading}`);
            return;
        }

        this.#isLoading = true;
        const containerQuery = this.constructor.ModalSelectors.CONTENT_CONTAINER(this.#modalName);
        l.log(`${this.#modalName}: Loading modal content into `, containerQuery);
        const container = document.querySelector(containerQuery);
        container.setAttribute('aria-busy', 'true');

        try {
            const qs = new URLSearchParams();
            qs.append('ms', this.#modalName);


            const stateQs = this.#queryStringManager.buildStateQueryString();
            for (const [key, value] of stateQs) {
                qs.append(key, value);
            }

            const userQs = this.#queryStringManager.buildUserQueryString(this.#modalName);
            for (const [key, value] of userQs) {
                qs.append(key, value);
            }

            l.log("qs", qs)
            const response = await ajax(`/toursearch/toursearchfiltermodals?${qs.toString()}`);
            container.innerHTML = response;
            this.#isLoaded = true;

            // Attach clear button event
            this.attachHandler(
                'modalClearButton',
                this.clearSelected,
                this.constructor.ModalSelectors.CLEAR_BUTTON(this.#modalName),
                ['click']
            );

            //Initialize other methods - tooltips, modals, etc.
            document.dispatchEvent(new CustomEvent('dynamicContentLoaded', {
                bubbles: true,
            }));
        } catch (error) {
            l.error(`${this.#modalName}: Error loading modal content:`, error);
            this.#isLoaded = false;
        } finally {
            container.setAttribute('aria-busy', 'false');
            this.#isLoading = false;
        }
    }

    getSelectedValues() {
        l.log(`${this.#modalName}: getSelectedToQueryString`);
        if (this.constructor.Selectors.INPUT.SELECTED === '') return [];

        const selectedValues = [];
        const cbQuery = this.constructor.Selectors.INPUT.SELECTED;

        document.querySelectorAll(cbQuery).forEach(cb => {
            if (cb.checked) {
                const val = this.formatSelectedValue(cb.value);
                l.log(`${this.#modalName}: selected checkbox`, val);
                selectedValues.push(val);
            }
        });

        return selectedValues;
    }

    formatSelectedValue(val) {
        return [this.constructor.Config.QUERY_STRING_KEY, val];
    }

    dispose() {
        l.log(`${this.#modalName}: disposing modal`);

        // First remove all registered event handlers
        this._eventHandlers.forEach(handlerInfo => {
            const { handler, elements, eventTypes } = handlerInfo;
            if (elements && eventTypes) {
                elements.forEach(element => {
                    if (element) {
                        eventTypes.forEach(eventType => {
                            element.removeEventListener(eventType, handler);
                        });
                    }
                });
            }
        });

        // Clear the handlers map
        this._eventHandlers.clear();

        // Reset state
        this.#isLoaded = false;
        this.#isLoading = false;
    }

    //dispose() {
    //    l.log(`${this.#modalName}: disposing modal`);

    //    // Remove all event listeners using the handlers map
    //    this._eventHandlers.forEach((handlerInfo, name) => {
    //        if (!handlerInfo) {
    //            l.warn(`${this.#modalName}: No handler info found for ${name}`);
    //            return;
    //        }

    //        const { handler, elements, eventTypes } = handlerInfo;

    //        if (!elements || !eventTypes) {
    //            l.warn(`${this.#modalName}: Missing elements or eventTypes for ${name}`);
    //            return;
    //        }

    //        l.log(`${this.#modalName}: Removing ${name} handlers from ${elements.length} elements`);

    //        elements.forEach(element => {
    //            if (element) {
    //                eventTypes.forEach(eventType => {
    //                    element.removeEventListener(eventType, handler);
    //                });
    //            }
    //        });
    //    });

    //    // Clear the handlers map
    //    this._eventHandlers.clear();

    //    // Reset state
    //    this.#isLoaded = false;
    //    this.#isLoading = false;
    //}
}